import { useEffect, useRef, useState } from 'react';
import { SortBy, sortable } from 'tea-component/lib/table/addons';
import pLimit from 'p-limit';
import { dealwithInfluenceTableData } from '../utils';
import { arrayToObj } from '@/pages/opinion/utils';
import { getInfluencerCreatorList } from '@op/services';
import { dealwithSortItem } from '../constants';

// 默认排序指标
const tableDefaultSortKey = 'followers';

// 默认的pageSize
const defaultPageSize = 20;

/**
 * 将对象 key 排序后序列化，用于判断参数内容是否真正发生变化。
 * 避免上游对象引用变化（但内容未变）导致重复请求。
 * @param obj
 * @returns
 */
function getStableKey(obj: any) {
  if (!obj) return '';
  return JSON.stringify(
    Object.keys(obj)
      .sort()
      .reduce((acc: any, key: string) => {
        acc[key] = obj[key];
        return acc;
      }, {}),
  );
}

/**
 * table数据的所有相关内容
 * @param params
 * @param mediaType
 * @param saveDefaultData
 * @param allSelectInfo
 * @returns
 */
export default function useTableData({
  params,
  mediaType,
  saveDefaultData,
  allSelectInfo,
  setLoadingInfo,
  defaultPageSize: propsDefaultPageSize,
  tableDefaultSortKey: propsTableDefaultSortKey,
}: any) {
  const [keyword, setKeyword] = useState('');
  const [tableRecords, setTableRecord] = useState<Array<any>>([]);
  const [chartMaxValue, setChartMaxValue] = useState<{ [key: string]: number }>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [apiError, setApiError] = useState(false);
  const [pageInfo, setPageInfo] = useState({
    page_size: propsDefaultPageSize ? propsDefaultPageSize : defaultPageSize,
    page: 1,
  });

  const tableDefaultSortKey = propsTableDefaultSortKey
    ? propsTableDefaultSortKey
    : 'followers';
  const [sort, setSort] = useState<SortBy<any>>({
    by: tableDefaultSortKey,
    order: 'desc',
  });
  const [linkTime, setLinkTime] = useState({
    new: { end_time: '', start_time: '' },
    old: { end_time: '', start_time: '' },
  });

  const getTableListData = (otherParams: any) => {
    if (Object.keys(params || {}).length > 0) {
      setLoading(true);
      setApiError(false);
      setTableRecord([]);
      // streaming取所有数据
      getInfluencerCreatorList(
        dealwithSortItem(
          {
            ...params,
            ...otherParams,
            anchor_name: keyword,
          },
          mediaType === 'stream',
        ),
      )
        .then((resultData: any) => {
          const {
            list,
            old_list,
            total,
            max_item,
            user_infos,
            old_plus_list,
            link_time,
            max_value,
          } = resultData || {};
          const localTableRecord =
            dealwithInfluenceTableData({
              new_list: list,
              old_list,
              old_plus_list,
              user_info_list: user_infos,
              max_item,
              media_type: mediaType,
            }) || [];

          if (mediaType !== 'stream') {
            setLoadingInfo(list);
          }
          setChartMaxValue(max_value || {});
          setTableRecord(localTableRecord);
          saveDefaultData(localTableRecord);
          setLinkTime(arrayToObj(link_time, 'key'));
          setTotal(total);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const addList = (list?: Array<Array<any>>) => {
    const result: Array<any> = [];
    const merge = (arr: any) => {
      for (let index = 0; index < arr.length; index++) {
        result[index] = (result[index] || []).concat(arr[index] || []);
      }
    };
    merge(list);
    return (newList?: Array<Array<any>>) => {
      if (newList) {
        merge(newList);
      } else {
        return result;
      }
    };
  };

  const getAllTableList = async (otherParams: any) => {
    setLoading(true);
    setApiError(false);
    const onePageSize = 5000;
    // 先做一个请求获取total
    const newParams = dealwithSortItem(
      {
        ...params,
        ...otherParams,
        anchor_name: keyword,
        page: 1,
        page_size: onePageSize,
        sort_item: tableDefaultSortKey,
        sort_model: 'desc',
      },
      mediaType === 'stream',
    );
    const data = await getInfluencerCreatorList(newParams);

    const {
      list,
      old_list,
      total,
      user_infos,
      old_plus_list,
      max_item,
      link_time,
      max_value,
    } = data || {
      total: 0,
    };
    const addListFn = addList([list, old_list, user_infos, old_plus_list]);
    const requestTotal = total - onePageSize;
    if (requestTotal > 0) {
      const page = Math.ceil(requestTotal / onePageSize) + 1;
      const curPLimit = pLimit(2);
      const requestAll: Array<Promise<any>> = [];
      for (let index = 2; index <= page; index++) {
        requestAll.push(
          curPLimit(() =>
            getInfluencerCreatorList({ ...newParams, page: index }),
          ),
        );
      }
      const resultAll = await Promise.all(requestAll);

      resultAll?.some((item) => {
        if (item?.code === 0) {
          const { list, old_list, user_infos, old_plus_list } =
            item?.data || {};
          addListFn([list, old_list, user_infos, old_plus_list]);
        } else {
          setApiError(true);
          return true;
        }
      });
    }
    const [allNewList, allOldList, allUserInfoList, oldPlusList] =
      addListFn() || [];
    const localTableRecord =
      dealwithInfluenceTableData({
        new_list: allNewList,
        old_list: allOldList,
        old_plus_list: oldPlusList,
        user_info_list: allUserInfoList,
        max_item,
        media_type: mediaType,
      }) || [];

    localTableRecord?.sort(
      sortable.comparer([{ by: 'followers_grow', order: 'desc' }]),
    );

    setTableRecord(localTableRecord);
    if (mediaType !== 'stream') {
      setLoadingInfo(localTableRecord.slice(0, defaultPageSize));
    }
    setChartMaxValue(max_value || {});
    saveDefaultData(localTableRecord);
    setLinkTime(arrayToObj(link_time, 'key'));
    setTotal(total);
    setLoading(false);
  };

  const countStartEnd = (
    localPageInfo = pageInfo,
    tableList = tableRecords,
    sortInfo = sort,
  ) => {
    const localSort =
      sortInfo.by === 'followers_grow_more_5k'
        ? { by: 'followers_grow', order: sortInfo.order }
        : sortInfo;
    tableList?.sort(sortable.comparer([localSort])) || [];

    const { page, page_size } = localPageInfo;
    const start = (page - 1) * page_size;
    const end = page * page_size;
    const list = tableList.slice(start, end);
    if (!list || list.length === 0) {
      return [];
    }
    return list;
  };

  // 是否发起请求
  const isRequest = useRef(false);
  // 记录上一次请求参数内容，用于拦截“引用变了但内容没变”的重复请求
  const lastRequestKeyRef = useRef('');
  useEffect(() => {
    if (isRequest.current) {
      const paramsInfo = {
        ...pageInfo,
        sort_item: sort.by,
        sort_model: sort.order,
      };
      getTableListData(paramsInfo);
    }
  }, [pageInfo]);

  const changePageInfo = (
    pageIndex = 1,
    pageSize: number = defaultPageSize,
    localSort = sort,
    noSetKeywordList = true,
  ) => {
    const isFollowersGrow = [
      'followers_grow_more_5k',
      'followers_grow',
    ].includes(localSort.by);

    // 直播和增长量排序都切换页面不需要请求数据
    isRequest.current = !isFollowersGrow;

    const localPageInfo = { page: pageIndex, page_size: pageSize };

    // 如果是增长量排序，那么就是设置keyword 的 loading值
    if (isFollowersGrow && noSetKeywordList && mediaType !== 'stream') {
      setLoadingInfo(countStartEnd(localPageInfo));
    }
    setPageInfo(localPageInfo);
  };

  const changeSortInfo = (localSort: SortBy<any>) => {
    // 随便那个不同就代表了排序变化
    if (sort.by !== localSort.by || sort.order !== localSort.order) {
      allSelectInfo.current = [];
      setSort(localSort);
      const isFollowersGrow = [
        'followers_grow_more_5k',
        'followers_grow',
      ].includes(localSort.by);
      changePageInfo(1, pageInfo.page_size, localSort, false);

      if (isFollowersGrow) {
        // 排序字段变了，需要重新请求
        if (localSort.by !== sort.by) {
          const paramsInfo =
            localSort.by === 'followers_grow_more_5k'
              ? {
                  followers: [
                    {
                      start_followers: 5000,
                      end_followers: 500000000,
                    },
                  ],
                }
              : {};
          getAllTableList(paramsInfo);
        } else if (localSort.order !== sort.order) {
          // 排序类型变了【倒序和正序】，需要请求keyword和切换有六个元素，但是不请求数据
          let list = countStartEnd(
            { page: 1, page_size: pageInfo.page_size },
            tableRecords,
            localSort,
          );
          saveDefaultData(list);
        }
      }
    }
  };

  useEffect(() => {
    // params 引用变化但内容没变（如标签配置异步返回导致对象重建）时不重复请求
    const requestKey = [
      getStableKey(params),
      keyword,
      mediaType,
    ].join('|');
    if (requestKey === lastRequestKeyRef.current) {
      return;
    }
    lastRequestKeyRef.current = requestKey;

    allSelectInfo.current = [];
    isRequest.current = false;
    setSort({ by: tableDefaultSortKey, order: 'desc' });
    setPageInfo({ page: 1, page_size: pageInfo.page_size });
    getTableListData({
      page: 1,
      page_size: pageInfo.page_size,
      sort_item: tableDefaultSortKey,
      sort_model: 'desc',
    });
  }, [params, keyword, mediaType]);

  // 排序联动能力
  // useEffect(() => {
  //   if (metric !== 'kolValue') {
  //     changeSortInfo({ order: 'desc', by: metric });
  //   }
  // }, [metric]);

  const getTableRecord = () => {
    return ['followers_grow_more_5k', 'followers_grow'].includes(sort.by)
      ? countStartEnd(pageInfo, tableRecords)
      : tableRecords;
  };

  return {
    sort,
    changeSortInfo,
    linkTime,
    total,
    apiError,
    pageInfo,
    changePageInfo,
    getTableRecord,
    keyword,
    setKeyword,
    chartMaxValue,
    loading,
  };
}
